import {
  AutotractorPropsType,
  AutotractorSlotIDs,
} from "common/components/map/layers/types";
import { bbox } from "@turf/turf";
import { buildPermission } from "portal/utils/auth";
import { ConfiguredJob, rtcJobsApi } from "common/state/rtcJobsApi";
import { DateTime } from "luxon";
import { derefPoint, getFieldBounds } from "common/utils/farm";
import { EMPTY_HISTORY } from "portal/state/location";
import { isWithinBounds } from "portal/utils/geo";
import {
  jobExplorer,
  JobSelectionType,
  Selection,
} from "common/state/jobExplorer";
import { JobType } from "protos/atk/job_type";
import { Layer, Source } from "react-map-gl";
import { lineString } from "@turf/helpers";
import { Map as MapView } from "portal/components/map/Map";
import { Objective, PivotRiskObjectivesResponse, Task } from "protos/rtc/jobs";
import {
  ObjectiveFeature,
  TaskFeature,
  TractorFeature,
} from "common/utils/rtcJobs";
import { ObjectiveUpdate, TaskUpdate, UpdateType } from "protos/rtc/web_socket";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { RigEffectiveConfig } from "protos/atk/effective_config";
import { RootState, useLocation } from "portal/state/store";
import { rtcLocatorApi } from "common/state/rtcLocatorApi";
import { theme } from "portal/theme/theme";
import {
  TractorLayers,
  useTractorLayers,
} from "common/components/map/layers/TractorLayers";
import { useAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import { useDispatch, useSelector } from "react-redux";
import { useFarmLayers } from "common/components/map/layers/FarmLayers";
import { useFarmMapData } from "common/hooks/useFarmMapData";
import {
  useJobSubscription,
  useObjectiveSubscription,
} from "common/hooks/useLiveJobData";
import { useLocationHistoryLayers } from "common/components/map/layers/LocationHistoryLayers";
import { useMapSlots } from "common/components/map/MapSlots";
import { useMultiPivotLayers } from "common/components/map/layers/MultiPivotLayers";
import {
  useObjectiveLayers,
  useObjectiveProgressOverlay,
} from "common/components/map/layers/ObjectiveLayers";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useStable } from "portal/utils/hooks/useStable";
import { useTaskLayers } from "common/components/map/layers/TaskLayers";
import { viteImageResolver } from "common/utils/map/imageResolver.vite";
import { Zone } from "protos/farm/farm";
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const SLOT_IDS = Object.values(AutotractorSlotIDs);
const NO_POINTS: string[] = [];

interface OrchestrationMapProps {
  configuredJob: ConfiguredJob;
  objectives: Objective[];
  rig: RigEffectiveConfig | undefined;
  showTractorHistory?: boolean;
  hideTasks?: boolean;
  showAllTasks?: boolean;
  showPivotForecast?: boolean;
  showFrontAxlePath?: boolean;
  pivotRiskData?: PivotRiskObjectivesResponse;
}

export const OrchestrationMap: FunctionComponent<OrchestrationMapProps> = ({
  configuredJob,
  objectives,
  showAllTasks,
  showTractorHistory,
  showPivotForecast,
  showFrontAxlePath,
  pivotRiskData,
  rig,
}) => {
  const { setFocus, setSelection } = jobExplorer.actions;
  const dispatch = useDispatch();
  const selection = useSelector(jobExplorer.selectors.selectSelection);
  const focus = useSelector(jobExplorer.selectors.selectFocus);
  const farm = useSelector(jobExplorer.selectors.selectFarm);

  const { data: availableTractors } = useQueryPopups(
    rtcLocatorApi.endpoints.listRobots.useQuery({
      serials: configuredJob.job.robotWhitelist?.entries.map(
        (r) => r.robotSerial
      ),
    })
  );
  const tractorSerials = useMemo(
    () => availableTractors?.robots.map((v) => v.serial) ?? [],
    [availableTractors]
  );
  const hiddenTractors = useSelector(
    jobExplorer.selectors.selectHiddenTractors
  );
  const highlightedObjectives = useSelector(
    jobExplorer.selectors.selectHighlightedObjectives
  );
  const assignmentsByTractorSerial = useSelector(
    jobExplorer.selectors.selectAssignmentsByTractorSerial
  );
  const colorsBySerial = useSelector(
    jobExplorer.selectors.selectColorsBySerial
  );

  // Carbon Technical users (read:customers:all) see internal task details such
  // as guide path error messages.
  const canReadCustomers = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.customers,
      PermissionDomain.all
    )
  );

  const farmMapData = useFarmMapData(farm);

  // todo: re-evaluate if we move to specifying co-located zones
  const field = useMemo(() => {
    return farm?.zones.find((z) => z.id?.id === configuredJob.job.fieldId);
  }, [farm, configuredJob.job.fieldId]);
  const { centerPivots, endpointIds } = useMemo(() => {
    return {
      centerPivots:
        field?.contents?.field?.centerPivotIds.flatMap((id) => {
          const zone = farmMapData?.zonesById[id.id];
          const pivot = zone?.contents?.centerPivot;
          if (farmMapData && pivot?.center && zone) {
            const z: Zone = {
              ...zone,
              contents: {
                centerPivot: {
                  ...pivot,
                  center: derefPoint(farmMapData.pointsById, pivot.center),
                },
              },
            };
            return z;
          }
          return zone ?? [];
        }) ?? [],
      endpointIds:
        field?.contents?.field?.centerPivotIds.flatMap((id) =>
          id.id
            ? farmMapData?.zonesById[
                id.id
              ]?.contents?.centerPivot?.legs.flatMap(
                (l) => l.endpointDeviceId
              ) ?? []
            : []
        ) ?? [],
    };
  }, [field, farmMapData]);

  const centerPivotData = useSelector((state: RootState) =>
    jobExplorer.selectors.selectEndpointDeviceDataByDeviceIds(
      state,
      endpointIds
    )
  );

  const zoomBounds = useMemo(() => {
    if (field && farmMapData?.pointsById) {
      return getFieldBounds(field, farmMapData.pointsById);
    }

    // For jobs without a field (e.g., Transport jobs), use the first objective's path
    const firstObjective = objectives[0];
    if (
      firstObjective?.data?.path?.points &&
      firstObjective.data.path.points.length > 0
    ) {
      const coordinates = firstObjective.data.path.points.map(
        (pt: { lng: number; lat: number }) => [pt.lng, pt.lat]
      );
      const line = lineString(coordinates);
      const [minLng, minLat, maxLng, maxLat] = bbox(line);
      return {
        minX: minLat,
        minY: minLng,
        maxX: maxLat,
        maxY: maxLng,
      };
    }

    return farmMapData?.bounds;
  }, [field, farmMapData, objectives]);

  const {
    Layers: FarmLayers,
    idsToLoad: farmIdsToLoad,
    interactionLayers: farmInteractionLayers,
  } = useFarmLayers({
    hoverZone: (feature) => {
      if (!feature?.properties.zoneId) {
        return;
      }
      const zone = farmMapData?.zonesById[feature.properties.zoneId];
      if (zone?.contents?.obstacle || !zone) {
        dispatch(
          setFocus(
            zone ? { type: JobSelectionType.ZONE, selection: zone } : undefined
          )
        );
      }
    },
    clickZone: (feature) => {
      if (!feature?.properties.zoneId) {
        return;
      }
      const zone = farmMapData?.zonesById[feature.properties.zoneId];
      if (zone?.contents?.obstacle || !zone) {
        dispatch(
          setSelection(
            zone ? { type: JobSelectionType.ZONE, selection: zone } : undefined
          )
        );
      }
    },
  });

  const enabledZoneIds = useMemo(() => {
    // if we can find co-located zones, use those, otherwise use everything
    // todo: as of writing, jobs only specify a field. We may consider changing it to specify
    // multiple zones
    const ids: string[] = [];
    if (field && farmMapData) {
      const fieldBounds = getFieldBounds(field, farmMapData.pointsById);
      if (farm?.zones && fieldBounds) {
        for (const zone of farm.zones) {
          if (
            zone.id?.id &&
            zone.areas[0]?.polygon?.boundary?.points[0]?.id?.id
          ) {
            const ptId = zone.areas[0].polygon.boundary.points[0].id.id;
            const pt = farmMapData.pointsById[ptId];
            const PADDING_METERS = 100;
            if (pt && isWithinBounds(pt, fieldBounds, PADDING_METERS)) {
              ids.push(zone.id.id);
            }
          }
        }
      }
    }
    return ids.length > 0 ? ids : undefined;
  }, [farm, field, farmMapData]);

  const {
    Layers: MultiPivotLayers,
    interactionLayers: multiPivotInteractionLayers,
    idsToLoad: multiPivotIdsToLoad,
  } = useMultiPivotLayers({
    clickPivot: (z) => {
      if (!z?.properties.zoneId) {
        return;
      }
      const zone = farmMapData?.zonesById[z.properties.zoneId];
      if (zone?.contents?.centerPivot || !zone) {
        dispatch(
          setSelection(
            zone ? { type: JobSelectionType.ZONE, selection: zone } : undefined
          )
        );
      }
    },
    hoverPivot: (z) => {
      if (!z?.properties.zoneId) {
        return;
      }
      const zone = farmMapData?.zonesById[z.properties.zoneId];
      if (zone?.contents?.centerPivot || !zone) {
        dispatch(
          setFocus(
            zone ? { type: JobSelectionType.ZONE, selection: zone } : undefined
          )
        );
      }
    },
  });

  const {
    idsToLoad: tractorIdsToLoad,
    interactionLayers: tractorInteractionLayers,
  } = useTractorLayers({
    serials: tractorSerials,
    onClick: (f?: TractorFeature) =>
      dispatch(
        setSelection(
          f?.properties.serial
            ? {
                selection: { serial: f.properties.serial },
                type: JobSelectionType.TRACTOR,
              }
            : undefined
        )
      ),
    onEnter: (f?: TractorFeature) =>
      dispatch(
        setFocus(
          f?.properties.serial
            ? {
                selection: { serial: f.properties.serial },
                type: JobSelectionType.TRACTOR,
              }
            : undefined
        )
      ),
    onLeave: () => dispatch(setFocus(undefined)),
  });

  const getObjOrTask = (
    f: ObjectiveFeature | TaskFeature | undefined
  ): Selection | undefined => {
    if (!f) {
      return undefined;
    }
    if (f.properties.type === AutotractorPropsType.OBJECTIVE) {
      return {
        type: JobSelectionType.OBJECTIVE,
        selection: f.properties.objective,
      };
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (f.properties.type === AutotractorPropsType.TASK) {
      return {
        type: JobSelectionType.TASK,
        selection: f.properties.task,
      };
    }
  };

  const {
    Layers: ObjectiveProgressOverlay,
    idsToLoad: progressIdsToLoad,
    interactionLayers: progressInteractionLayers,
  } = useObjectiveProgressOverlay({
    onEnter: (f) => {
      dispatch(setFocus(getObjOrTask(f)));
    },
    onClick: (f) => {
      dispatch(setSelection(getObjOrTask(f)));
    },
    onLeave: () => dispatch(setFocus(undefined)),
  });

  const {
    Layers: AssignmentLayers,
    idsToLoad: objectiveIdsToLoad,
    interactionLayers: objectiveInteractionLayers,
  } = useObjectiveLayers({
    onEnter: (f) => {
      dispatch(setFocus(getObjOrTask(f)));
    },
    onClick: (f) => {
      dispatch(setSelection(getObjOrTask(f)));
    },
    onLeave: () => dispatch(setFocus(undefined)),
  });

  const {
    Layers: TaskLayers,
    idsToLoad: taskIdsToLoad,
    interactionLayers: taskInteractionLayers,
  } = useTaskLayers({
    onEnter: (f) => {
      dispatch(setFocus(getObjOrTask(f)));
    },
    onClick: (f) => {
      dispatch(setSelection(getObjOrTask(f)));
    },
    onLeave: () => dispatch(setFocus(undefined)),
  });

  const interactionLayers = useMemo(() => {
    return [
      ...farmInteractionLayers,
      ...tractorInteractionLayers,
      ...objectiveInteractionLayers,
      ...taskInteractionLayers,
      ...multiPivotInteractionLayers,
      ...progressInteractionLayers,
    ];
  }, [
    farmInteractionLayers,
    tractorInteractionLayers,
    progressInteractionLayers,
    objectiveInteractionLayers,
    taskInteractionLayers,
    multiPivotInteractionLayers,
  ]);

  const mapSlots = useMapSlots(SLOT_IDS);

  const [tasks, setTasks] = useState<Task[]>();
  const tasksThisJob = useMemo(
    () => tasks?.filter((t) => t.jobId === configuredJob.job.id),
    [configuredJob.job.id, tasks]
  );
  const [listTasks] = rtcJobsApi.endpoints.listTasks.useLazyQuery();
  const onObjectiveUpdate = useCallback(
    (o: ObjectiveUpdate) => {
      if (o.objective) {
        const objectiveId = o.objective.id;
        listTasks({ objectiveIds: [objectiveId], pageSize: 15_000 }).then(
          ({ data }) => {
            // Merge the refetched tasks into existing state. Replacing wholesale drops
            // tasks belonging to other objectives and makes their map outlines blink.
            setTasks((prev) => [
              ...(prev ?? []).filter((t) => t.objectiveId !== objectiveId),
              ...(data?.tasks ?? []),
            ]);
          }
        );
      }
    },
    [listTasks]
  );
  const onTaskUpdate = useCallback((t: TaskUpdate) => {
    if (t.task) {
      const task = t.task;
      setTasks((prev) => {
        const current = prev ?? [];
        if (
          t.type === UpdateType.UPDATE_TYPE_CREATE &&
          !current.some((ot) => ot.id === task.id)
        ) {
          return [...current, task];
        }
        return current.map((ot) => (ot.id === task.id ? task : ot));
      });
    }
  }, []);
  const { objectiveId, skip } = useMemo(
    () => ({
      objectiveId:
        selection?.type === JobSelectionType.OBJECTIVE
          ? `${selection.selection.id}`
          : "0",
      skip: selection?.type !== JobSelectionType.OBJECTIVE,
    }),
    [selection]
  );
  useObjectiveSubscription({
    objectiveId,
    onObjectiveUpdate,
    onTaskUpdate,
    skip,
  });

  useJobSubscription({
    jobId: configuredJob.job.id.toString(),
    onObjectiveUpdate,
  });

  // initial fetch. Keyed on ids: objective updates replace the array without
  // changing which objectives are in it.
  const objectiveIdsKey = objectives.map((o) => o.id).join(",");
  useEffect(() => {
    // an empty objective_ids filter lists every task the user can see
    if (!objectiveIdsKey) {
      return;
    }
    const ac = new AbortController();
    const fetch = async (): Promise<void> => {
      const { data } = await listTasks({
        objectiveIds: objectiveIdsKey.split(",").map(Number),
        pageSize: 15_000,
      });
      if (ac.signal.aborted) {
        return;
      }
      setTasks(data?.tasks);
    };
    fetch();
    return () => ac.abort();
  }, [listTasks, objectiveIdsKey]);

  // Reordering a robot's assigned objectives re-plans the first task of each
  // affected objective (the goToReversiblePath task). Diff the assignments
  // and refetch tasks for any objective whose assignment (robot, position, or
  // timestamp) changed. The net effect of this is that end-of-row turnarounds
  // are refreshed in the UI whenever assignment order is changed.
  const prevAssignmentSignaturesRef = useRef<Map<number, string>>();
  useEffect(() => {
    const signatures = new Map<number, string>();
    for (const [serial, assignments] of Object.entries(
      assignmentsByTractorSerial
    )) {
      for (const [index, assignment] of assignments.entries()) {
        signatures.set(
          assignment.objectiveId,
          `${serial}::${index}::${assignment.updatedAt}`
        );
      }
    }
    const prev = prevAssignmentSignaturesRef.current;
    prevAssignmentSignaturesRef.current = signatures;
    if (!prev) {
      // First run: the mount-time fetch above already loads everything.
      return;
    }
    const changed = new Set<number>();
    for (const [objectiveId, signature] of signatures) {
      if (prev.get(objectiveId) !== signature) {
        changed.add(objectiveId);
      }
    }
    for (const objectiveId of prev.keys()) {
      if (!signatures.has(objectiveId)) {
        changed.add(objectiveId);
      }
    }
    if (changed.size === 0) {
      return;
    }
    listTasks({ objectiveIds: [...changed], pageSize: 15_000 }).then(
      ({ data }) => {
        setTasks((prevTasks) => [
          ...(prevTasks ?? []).filter((t) => !changed.has(t.objectiveId)),
          ...(data?.tasks ?? []),
        ]);
      }
    );
  }, [assignmentsByTractorSerial, listTasks]);

  const assignedObjectiveIdsForSelectedTractor =
    selection?.type === JobSelectionType.TRACTOR
      ? new Set(
          (assignmentsByTractorSerial[selection.selection.serial] ?? []).map(
            (a) => a.objectiveId
          )
        )
      : undefined;

  // Reverse mapping: objectiveId → robotSerial
  const objectiveIdToRobotSerial = useMemo(() => {
    const map = new Map<number, string>();
    for (const [serial, assignments] of Object.entries(
      assignmentsByTractorSerial
    )) {
      for (const assignment of assignments) {
        map.set(assignment.objectiveId, serial);
      }
    }
    return map;
  }, [assignmentsByTractorSerial]);

  // Laser weeding: when an objective is selected, show tasks for all objectives
  // assigned to the same robot so the end-of-row turnarounds between them show
  const assignedObjectiveIdsForSelectedObjective =
    selection?.type === JobSelectionType.OBJECTIVE &&
    configuredJob.job.type === JobType.LASER_WEED
      ? (() => {
          const robotSerial = objectiveIdToRobotSerial.get(
            selection.selection.id
          );
          if (!robotSerial) {
            return;
          }
          return new Set(
            (assignmentsByTractorSerial[robotSerial] ?? []).map(
              (a) => a.objectiveId
            )
          );
        })()
      : undefined;

  const matchingTasks =
    (showAllTasks
      ? tasksThisJob
      : tasksThisJob?.filter((t) => {
          return (
            (selection?.type === JobSelectionType.OBJECTIVE &&
              selection.selection.id === t.objectiveId) ||
            (selection?.type === JobSelectionType.TASK &&
              selection.selection.id === t.id) ||
            assignedObjectiveIdsForSelectedTractor?.has(t.objectiveId) ===
              true ||
            assignedObjectiveIdsForSelectedObjective?.has(t.objectiveId) ===
              true
          );
        })) ?? [];
  // stable references so the memoized layers below skip re-rendering
  const shownTasks = useStable(
    matchingTasks,
    (a, b) => a.length === b.length && a.every((t, i) => t === b[i])
  );
  const farmsMapData = useMemo(
    () => (farmMapData ? [farmMapData] : []),
    [farmMapData]
  );
  const selectedZoneId =
    selection?.type === JobSelectionType.ZONE
      ? selection.selection.id?.id
      : undefined;
  const selectedZoneIds = useMemo(
    () => (selectedZoneId ? [selectedZoneId] : []),
    [selectedZoneId]
  );
  const focusedZoneId =
    focus?.type === JobSelectionType.ZONE ? focus.selection.id?.id : undefined;
  const focusedZoneIds = useMemo(
    () => (focusedZoneId ? [focusedZoneId] : []),
    [focusedZoneId]
  );

  const mapView = useSelector(jobExplorer.selectors.selectMapView);

  return (
    <MapView
      allowEmpty
      extraBounds={zoomBounds}
      interactionLayers={interactionLayers}
      hideHoverInfo
      imageConfig={{
        resolver: viteImageResolver,
        idsToLoad: [
          ...multiPivotIdsToLoad,
          ...farmIdsToLoad,
          ...objectiveIdsToLoad,
          ...taskIdsToLoad,
          ...tractorIdsToLoad,
          ...progressIdsToLoad,
        ],
      }}
      extraControls={
        <>
          {mapSlots}
          {farmMapData && (
            <FarmLayers
              beforeId={AutotractorSlotIDs.FARMS}
              interactableZoneIds={enabledZoneIds}
              shownPoints={NO_POINTS}
              selectedZoneIds={selectedZoneIds}
              focusedZoneIds={focusedZoneIds}
              hidePivots
              farmsMapData={farmsMapData}
              slotIds={SLOT_IDS}
            />
          )}
          <MultiPivotLayers
            beforeId={AutotractorSlotIDs.PIVOTS}
            liveData={centerPivotData}
            zones={centerPivots}
            focusedZoneIds={focusedZoneIds}
            selectedZoneIds={selectedZoneIds}
          />
          {availableTractors && <LiveTractorLayers serials={tractorSerials} />}
          {mapView === "objectives" && (
            <ObjectiveProgressOverlay
              beforeId={AutotractorSlotIDs.JOB_OBJECTIVES}
              configuredJob={configuredJob}
              objectives={objectives}
              focusedTractor={
                focus?.type === JobSelectionType.TRACTOR
                  ? focus.selection.serial
                  : undefined
              }
              selectedObjective={
                selection?.type === JobSelectionType.OBJECTIVE
                  ? selection.selection.id
                  : undefined
              }
              focusedObjective={
                focus?.type === JobSelectionType.OBJECTIVE
                  ? focus.selection.id
                  : undefined
              }
              highlightedObjectives={highlightedObjectives}
              assignmentsByTractorSerial={assignmentsByTractorSerial}
            />
          )}
          {mapView === "robots" && (
            <AssignmentLayers
              beforeId={AutotractorSlotIDs.JOB_OBJECTIVES}
              configuredJob={configuredJob}
              objectives={objectives}
              focusedTractor={
                focus?.type === JobSelectionType.TRACTOR
                  ? focus.selection.serial
                  : undefined
              }
              hiddenTractors={hiddenTractors}
              selectedObjective={
                selection?.type === JobSelectionType.OBJECTIVE
                  ? selection.selection.id
                  : undefined
              }
              focusedObjective={
                focus?.type === JobSelectionType.OBJECTIVE
                  ? focus.selection.id
                  : undefined
              }
              highlightedObjectives={highlightedObjectives}
              colorsBySerial={colorsBySerial}
              assignmentsByTractorSerial={assignmentsByTractorSerial}
            />
          )}
          <TaskLayers
            configuredJob={configuredJob}
            beforeId={AutotractorSlotIDs.JOB_TASKS}
            tasks={shownTasks}
            showTasks={true}
            rig={rig}
            // don't render tasks at low zoom to reduce visual noise -- except
            // for transport jobs, where tasks are the only thing on the map
            minZoom={configuredJob.transport ? undefined : 14}
            showTechnicalDetails={canReadCustomers}
            followPathDisplay={showFrontAxlePath ? "frontAxle" : "implement"}
          />
          {showTractorHistory && (
            <TractorHistoryLayers
              serials={configuredJob.job.robotWhitelist?.entries.map(
                (r) => r.robotSerial
              )}
              colorsBySerial={colorsBySerial}
            />
          )}
          {showPivotForecast && pivotRiskData && (
            <>
              {pivotRiskData.futurePivotPositions.map((location, index) => (
                <PivotPredictionLayer
                  key={`future-${index}`}
                  lineString={location}
                  color={theme.colors.red["500"]}
                  label={`Future ${index + 1}`}
                  index={index}
                  beforeId={AutotractorSlotIDs.JOB_TASKS}
                />
              ))}
              {pivotRiskData.increasingRiskObjectives.length > 0 && (
                <AtRiskObjectiveLayer
                  objectives={pivotRiskData.increasingRiskObjectives}
                  color={theme.colors.red["500"]}
                  layerId="increasing-risk-objectives"
                  beforeId={AutotractorSlotIDs.JOB_TASKS}
                />
              )}
              {pivotRiskData.decreasingRiskObjectives.length > 0 && (
                <AtRiskObjectiveLayer
                  objectives={pivotRiskData.decreasingRiskObjectives}
                  color={theme.colors.yellow["500"]}
                  layerId="decreasing-risk-objectives"
                  beforeId={AutotractorSlotIDs.JOB_TASKS}
                />
              )}
            </>
          )}
        </>
      }
    ></MapView>
  );
};

interface LiveTractorLayersProps {
  serials: string[];
}

// subscribes to live positions itself so position updates only re-render
// the tractors, not the rest of the map
const LiveTractorLayers: FunctionComponent<LiveTractorLayersProps> = ({
  serials,
}) => {
  const { setSelection } = jobExplorer.actions;
  const dispatch = useDispatch();
  const selection = useSelector(jobExplorer.selectors.selectSelection);
  const focus = useSelector(jobExplorer.selectors.selectFocus);
  const hiddenTractors = useSelector(
    jobExplorer.selectors.selectHiddenTractors
  );
  const tractorData = useSelector((state: RootState) =>
    jobExplorer.selectors.selectTractorDataBySerials(state, serials)
  );
  return (
    <>
      {serials
        .filter((s) => !hiddenTractors.includes(s))
        .map(
          (s) =>
            tractorData[s] && (
              <TractorLayers
                liveData={{
                  location: tractorData[s].liveTractorData,
                  isMoving: Boolean(tractorData[s].tractorIsMoving),
                  isStale: Boolean(tractorData[s].tractorIsStale),
                  lastCheckedTimestamp: DateTime.now().toISO(),
                }}
                beforeId={AutotractorSlotIDs.OBSTACLES}
                key={s}
                serial={s}
                color={tractorData[s].color}
                isSelected={
                  selection?.type === JobSelectionType.TRACTOR &&
                  selection.selection.serial === s
                }
                onClick={() =>
                  dispatch(
                    setSelection({
                      type: JobSelectionType.TRACTOR,
                      selection: { serial: s },
                    })
                  )
                }
                isFocused={
                  focus?.type === JobSelectionType.TRACTOR &&
                  focus.selection.serial === s
                }
              />
            )
        )}
    </>
  );
};

interface TractorHistoryLayersProps {
  serials: string[] | undefined;
  colorsBySerial: Record<string, string>;
}

// subscribes to location history itself for the same reason as LiveTractorLayers
const TractorHistoryLayers: FunctionComponent<TractorHistoryLayersProps> = ({
  serials,
  colorsBySerial,
}) => {
  const { history } = useLocation();
  const { Layers: LocationHistoryLayers } = useLocationHistoryLayers();
  return (
    <>
      {serials?.map((serial) => (
        <LocationHistoryLayers
          key={serial}
          {...(history[serial] ?? EMPTY_HISTORY)}
          entityId={serial}
          beforeId={AutotractorSlotIDs.JOB_TASKS}
          color={colorsBySerial[serial]}
        />
      ))}
    </>
  );
};

interface PivotPredictionLayerProps {
  lineString: { points: Array<{ lng: number; lat: number }> };
  color: string;
  label: string;
  index: number;
  beforeId?: string;
}

const PivotPredictionLayer: FunctionComponent<PivotPredictionLayerProps> = ({
  lineString,
  color,
  index,
  beforeId,
}) => {
  const geojsonData = useMemo(() => {
    if (lineString.points.length < 2) {
      return;
    }
    return {
      type: "FeatureCollection" as const,
      features: [
        {
          type: "Feature" as const,
          properties: {},
          geometry: {
            type: "LineString" as const,
            coordinates: lineString.points.map((p) => [p.lng, p.lat]),
          },
        },
      ],
    };
  }, [lineString]);

  if (!geojsonData) {
    return;
  }

  return (
    <>
      <Source
        id={`pivot-prediction-${index}`}
        type="geojson"
        data={geojsonData}
      >
        <Layer
          id={`pivot-prediction-line-${index}`}
          type="line"
          paint={{
            "line-color": color,
            "line-width": 4,
            "line-opacity": 0.8,
          }}
          beforeId={beforeId}
        />
      </Source>
    </>
  );
};

interface AtRiskObjectiveLayerProps {
  objectives: Array<{
    id: number;
    data?: {
      abLine?: {
        a: { lat: number; lng: number };
        b: { lat: number; lng: number };
      };
    };
  }>;
  color?: string;
  layerId?: string;
  beforeId?: string;
}

const AtRiskObjectiveLayer: FunctionComponent<AtRiskObjectiveLayerProps> = ({
  objectives,
  color = theme.colors.red["500"],
  layerId,
  beforeId,
}) => {
  const geojsonData = useMemo(() => {
    const features = objectives
      .filter((obj) => obj.data?.abLine)
      .map((obj) => ({
        type: "Feature" as const,
        properties: { objectiveId: obj.id },
        geometry: {
          type: "LineString" as const,
          coordinates: [
            [obj.data!.abLine!.a.lng, obj.data!.abLine!.a.lat],
            [obj.data!.abLine!.b.lng, obj.data!.abLine!.b.lat],
          ],
        },
      }));

    if (features.length === 0) {
      return;
    }

    return {
      type: "FeatureCollection" as const,
      features,
    };
  }, [objectives]);

  if (!geojsonData) {
    return;
  }

  return (
    <>
      <Source id={layerId} type="geojson" data={geojsonData}>
        <Layer
          id={`${layerId}-glow-outer`}
          type="line"
          paint={{
            "line-color": color,
            "line-width": 48,
            "line-opacity": 0.15,
            "line-blur": 12,
          }}
          beforeId={beforeId}
        />
        <Layer
          id={`${layerId}-glow-inner`}
          type="line"
          paint={{
            "line-color": color,
            "line-width": 24,
            "line-opacity": 0.25,
            "line-blur": 6,
          }}
          beforeId={beforeId}
        />
      </Source>
    </>
  );
};
